import { useCallback, useState } from 'react';

import {
  DeleteAccountFailure,
  requestAccountDeletion,
  updateProfile,
  type LanguagePreference,
  type UpdateProfileInput,
  type UserRow,
} from '@/lib/db/buyer';
import { signOut, updatePassword } from '@/lib/auth-actions';

// ---------------------------------------------------------------------------
// useUpdateProfile — UPDATE users (first_name / last_name / phone / language)
// ---------------------------------------------------------------------------

export type UpdateProfileStatus = 'idle' | 'saving' | 'error';

export function useUpdateProfile(): {
  status: UpdateProfileStatus;
  update: (input: UpdateProfileInput) => Promise<UserRow | null>;
} {
  const [status, setStatus] = useState<UpdateProfileStatus>('idle');

  const update = useCallback(async (input: UpdateProfileInput): Promise<UserRow | null> => {
    setStatus('saving');
    try {
      const row = await updateProfile(input);
      setStatus('idle');
      return row;
    } catch (err) {
      setStatus('error');
      throw err instanceof Error ? err : new Error(String(err));
    }
  }, []);

  return { status, update };
}

// ---------------------------------------------------------------------------
// useChangePassword — wrap auth-actions.updatePassword (ADR-025 §G)
// ---------------------------------------------------------------------------
// Supabase Auth does NOT validate the current password — the UI accordingly
// surfaces ONE input (`newPassword`) plus a confirmation field. The Zod
// schema for that form lives in the page; this hook is intentionally thin.

export type ChangePasswordStatus = 'idle' | 'saving' | 'error';

export function useChangePassword(): {
  status: ChangePasswordStatus;
  change: (newPassword: string) => Promise<void>;
} {
  const [status, setStatus] = useState<ChangePasswordStatus>('idle');

  const change = useCallback(async (newPassword: string): Promise<void> => {
    setStatus('saving');
    try {
      await updatePassword(newPassword);
      setStatus('idle');
    } catch (err) {
      setStatus('error');
      throw err instanceof Error ? err : new Error(String(err));
    }
  }, []);

  return { status, change };
}

// ---------------------------------------------------------------------------
// useDeleteAccountRequest — invoke Edge + sign out + redirect
// ---------------------------------------------------------------------------
// Returns `{ status, run }`. The hook does NOT navigate by itself — the page
// passes a `redirectTo` callback so MemoryRouter stays test-friendly. The
// caller is responsible for closing the confirm modal.

export type DeleteAccountStatus = 'idle' | 'pending' | 'error';

export function useDeleteAccountRequest(): {
  status: DeleteAccountStatus;
  run: (redirectTo: () => void) => Promise<DeleteAccountFailure | null>;
} {
  const [status, setStatus] = useState<DeleteAccountStatus>('idle');

  const run = useCallback(async (redirectTo: () => void): Promise<DeleteAccountFailure | null> => {
    setStatus('pending');
    try {
      await requestAccountDeletion();
      try {
        await signOut();
      } catch {
        // signOut failure is non-fatal — the user is anonymised server-side
        // and AuthContext rejects further sessions on next refresh.
      }
      redirectTo();
      setStatus('idle');
      return null;
    } catch (err) {
      setStatus('error');
      if (err instanceof DeleteAccountFailure) return err;
      return new DeleteAccountFailure(
        'internal_error',
        err instanceof Error ? err.message : String(err),
      );
    }
  }, []);

  return { status, run };
}

// ---------------------------------------------------------------------------
// Language preference mapping — UI uppercase ↔ DB lowercase enum
// ---------------------------------------------------------------------------

export const UI_LANG_TO_DB: Record<'EN' | 'FR' | 'AR', LanguagePreference> = {
  EN: 'en',
  FR: 'fr',
  AR: 'ar',
};

export const DB_LANG_TO_UI: Record<LanguagePreference, 'EN' | 'FR' | 'AR'> = {
  en: 'EN',
  fr: 'FR',
  ar: 'AR',
};
